import { FocusEvent } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useGetQuoteAffiliates } from "../../api/useQuotes";
import { useDebouncedDraft } from "../../shared/useDebouncedDraft";

type Props = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  // Bump this (e.g. pass the loaded quote itself) when `value` can change out from under the
  // field for a reason other than its own typing - a fetch loading in, a reset on another
  // control's toggle - so the local draft resyncs instead of quietly keeping a stale value. Not
  // needed where `value` is only ever seeded once at mount.
  resetKey?: unknown;
};

// Issue 31: the affiliate name field. Recurring affiliates should keep a consistent spelling, so
// this is a free-solo autocomplete fed by the names already used on quotes, and every entry is
// upper-cased as it is typed (the API also trims + upper-cases on save).
// Issue 47: left-trim as the user types too, so a leading space can never be entered (interior
// spaces in a multi-word affiliate name are kept; the API trims trailing whitespace on save).
// Locally drafted (Issue 43 pattern) so a keystroke here doesn't touch the caller's own state.
const AffiliateNameField = ({ value, onChange, disabled, resetKey }: Props) => {
  const affiliates = useGetQuoteAffiliates();
  const draft = useDebouncedDraft(value, onChange, resetKey);
  return (
    <Autocomplete
      freeSolo
      options={affiliates.data || []}
      inputValue={draft.draft}
      disabled={disabled}
      onInputChange={(_event, next) => draft.onDraftChange(next.replace(/^\s+/, "").toUpperCase())}
      renderInput={params => (
        <TextField
          {...params}
          label="Affiliate Name (Commission Report only)"
          inputProps={{ ...params.inputProps, maxLength: 200, onBlur: (event: FocusEvent<HTMLInputElement>) => {
            params.inputProps.onBlur?.(event); draft.onBlur();
          } }}
          helperText={disabled ? undefined : `${draft.draft.length}/200`}
        />
      )}
    />
  );
};

export default AffiliateNameField;
